import type { components } from '@/generated/internal-api/openapi-types';
import type { AnimalAlertV1 } from '@/model/Alert.model';
import type { AnimalApiId } from '@/model/Animal.model';
import type { ID, ISODateTime, Nullable } from '@/model/Common.model';
import type { DataTableColumnFillRequestV1, DataTableV1 } from '@/model/DataTable.model';
import type { DataTableEventV1ActiveUsers, DataTableEventV1SelectionChange } from '@/model/DataTableEvent.model';
import type { StudyApiId } from '@/model/Study.model';
import type { AxiosInstance } from 'axios';
import type { Observable } from 'rxjs';
import { _notNil } from '@/littledash.ts';

export type DataTableApiId = components['schemas']['DataTableApiId.schema'];
export type DataTableColumnApiId = components['schemas']['DataTableColumnApiId.schema'];
export type DataTableRowApiId = components['schemas']['DataTableRowApiId.schema'];
export type DataTableColumnIndex = number;
export type DataTableRowIndex = number;

export type DataTableType = components['schemas']['DataTableType.schema'];
export type DataTableTimeUnit = 'hours' | 'minutes' | 'seconds';
export type DataTablePkIntervalTimeUnit = components['schemas']['DataTablePkColumnIntervalTimeUnitType.schema'];
export type DataTableCellReference = `[${DataTableColumnApiId},${DataTableRowApiId}]`;
export type DataTableRightBorderType = 'normal' | 'group-separator';

export type DataTableColumnExpansionRequest = components['schemas']['DataTableColumnExpansionUpdateRequestV1.schema'];
export type DataTableColumnExpansionRequestColumns = DataTableColumnExpansionRequest['columns'];
export type DataTableColumnExpansionResponse = components['schemas']['DataTableColumnExpansionUpdateResponseV1.schema'];
export type DataTableColumnExpansionResponseAffectedColumns = DataTableColumnExpansionResponse['affected_columns'];
export type DataTableDisplayColumnType = components['schemas']['DataTableDisplayColumnType.schema'];
export type DataTableSettingsV1 = components['schemas']['DataTableSettingsV1.schema'];

export type DataTableErrors = Array<{
  ref: DataTableCellReference;
  coordinate?: DataTableCellIdCoordinate;
  status?: DataTableCellStatus;
}>;
export type DataTableDirection = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';

export interface DataTableCellSelectionOptions {
  append?: boolean;
  scrollTo?: boolean;
  activate?: boolean;
  source?: DataTableCellSelectionChangeEventDetail['source'];
}

export interface DataTableCellsUpdateOptions {
  snapshot?: 'normal' | 'undo' | 'redo' | 'none';
  windowClear?: {
    startColumnId: string;
    startRowId: string;
    endColumnId: string;
    endRowId: string;
  };
}

export interface DataTableMoveSelectionOptions {
  append?: boolean;
}

export interface DataTableUpdateDisplayedRowHeadersOptions {
  persist?: boolean;
}

/**
 * Action to expand or collapse a column group
 */
export interface DataTableColumnGroupExpansionAction {
  type: 'expand' | 'collapse';
  column_api_id: DataTableColumnApiId;
}

export interface DataTableDimensions {
  cellWidth: number;
  cellHeight: number;
  columnHeight: number;
}

export interface DataTableApiServiceProps {
  studyId: StudyApiId;
  dataTableId: DataTableApiId;
  axios: AxiosInstance;
}

export type DataTable = components['schemas']['DataTableV1.schema'];

export interface DataTableApiServiceOperation<Source, Output> {
  (source: Observable<Source>): Observable<Output>;
}

export interface DataTableUpsertDetail {
  cells: Array<DataTableCellUpsert>;
  alerts: Array<DataTableAlert>;
  invalidCells: Array<DataTableCellUpsertError>;
  networkErrors: Array<DataTableCellUpsert>;
  effects: Array<DataTableColumnApiId>;
}

export interface DataTableApiService {
  loadTable(context: DataTableService): DataTableApiServiceOperation<unknown, DataTableV1>;

  loadCells(
    context: DataTableService
  ): DataTableApiServiceOperation<
    DataTableCellSelection<DataTableColumnApiId, DataTableRowApiId> & { include_alerts?: boolean },
    Array<DataTableApiLoadCellResponse>
  >;

  loadCellsByCoordinates(
    context: DataTableService
  ): DataTableApiServiceOperation<
    { coordinates: Array<DataTableCellIdCoordinate> },
    Array<DataTableApiLoadCellResponse>
  >;

  updateCells(
    context: DataTableService
  ): DataTableApiServiceOperation<Array<DataTableCellUpsert>, DataTableCellUpsertResponse>;

  validate(
    context: DataTableService
  ): DataTableApiServiceOperation<DataTableWindowClearRequest, DataTableValidateResponse>;

  updateSelection(context: DataTableService): DataTableApiServiceOperation<DataTableSelectionUpdate, void>;

  clearWindow(
    context: DataTableService
  ): DataTableApiServiceOperation<DataTableWindowClearRequest, DataTableWindowClearResponse>;

  updateTableSettings(context: DataTableService): DataTableApiServiceOperation<DataTableSettingsV1, void>;

  moveColumn(
    context: DataTableService
  ): DataTableApiServiceOperation<DataTableColumnMoveRequest, DataTableMoveColumnResponse>;

  addColumn(context: DataTableService): DataTableApiServiceOperation<DataTableColumnAddRequest, Array<DataTableColumn>>;

  updateColumn(context: DataTableService): DataTableApiServiceOperation<DataTableColumnUpdateRequest, DataTableColumn>;

  removeColumn(
    context: DataTableService
  ): DataTableApiServiceOperation<DataTableColumnApiId, DataTableColumnRemoveResponse>;

  addRow(context: DataTableService): DataTableApiServiceOperation<DataTableRowAddRequest, Array<DataTableRow>>;

  removeRow(context: DataTableService): DataTableApiServiceOperation<DataTableRowApiId, void>;

  fillColumn(
    context: DataTableService
  ): DataTableApiServiceOperation<
    { column_id: DataTableColumnApiId; request: DataTableColumnFillRequestV1 },
    DataTableCellUpsertResponse
  >;

  /**
   * Updates the expansion state of column groups via the backend API.
   *
   * @param context - The DataTableService instance providing access to table state and configuration
   * @returns An API service operation that accepts column expansion request data and returns the affected columns
   *
   * @remarks
   * This operation communicates with the backend to expand or collapse column groups.
   * The server controls which child columns are displayed, as not all child columns
   * may be hidden on collapse. The response includes all columns affected by the expansion change.
   *
   * @see {@link DataTableColumnExpansionRequestColumns} for the request payload structure
   * @see {@link DataTableColumnExpansionResponseAffectedColumns} for the response structure
   */
  updateColumnExpansion(
    context: DataTableService
  ): DataTableApiServiceOperation<
    DataTableColumnExpansionRequestColumns,
    DataTableColumnExpansionResponseAffectedColumns
  >;
}

export interface DataTableService {
  get name(): Readonly<string | undefined>;

  get tableId(): Readonly<DataTableApiId | undefined>;

  get studyApiId(): Readonly<StudyApiId>;

  get type(): Readonly<DataTableType | 'unknown'>;

  get unit(): Readonly<string | null | undefined>;

  get readonly(): Readonly<boolean>;

  get columns(): Readonly<Array<DataTableColumn>>;

  get leafColumns(): Readonly<Array<DataTableColumn>>;

  get rows(): Readonly<Array<DataTableRow>>;

  get columnCount(): number;

  get rowCount(): number;

  get dimensions(): Readonly<DataTableDimensions>;

  get selection(): Readonly<DataTableSelection | undefined>;

  get working(): Readonly<boolean>;

  get workflowService(): Readonly<DataTableWorkflowService>;

  initialise(): Promise<DataTableService>;

  hasColumn(columnId: DataTableColumnApiId): boolean;

  toIndexCoordinate(cellCoordinate: DataTableCellIdCoordinate): DataTableCellIndexCoordinate | undefined;

  toIndexColumn(columnId: DataTableColumnApiId): DataTableColumnIndex | undefined;

  toIndexRow(rowId: DataTableRowApiId): DataTableRowIndex | undefined;

  columnByIndex(index: number | undefined): DataTableColumn | undefined;

  columnById(columnId: DataTableColumnApiId | undefined): DataTableColumn | undefined;

  columnsByIds(columnIds: Array<DataTableColumnApiId> | undefined): Array<DataTableColumn>;

  rowByIndex(index: number | undefined): DataTableRow | undefined;

  fromIndexCoordinate(cellCoordinate: DataTableCellIndexCoordinate): DataTableCellIdCoordinate | undefined;

  cellSelected(cellCoordinate: DataTableCellIndexCoordinate): boolean;

  columnSelected(column: number): boolean;

  rowSelected(row: number): boolean;

  cellState(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellState;

  cellStatus(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellStatus;

  rowHasError(row: DataTableRowApiId | undefined): boolean;

  rowHeader(id: DataTableRowApiId | undefined): DataTableRow | undefined;

  cellData(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellDataCache | undefined;

  cellAlerts(cellCoordinate: DataTableCellIdCoordinate | undefined): DataTableCellAlertCache | undefined;

  handleChunkCellUpsert(
    chunkSize: number,
    validCells: Array<DataTableCellUpsert>
  ): Array<Promise<DataTableUpsertDetail>>;

  updateCells(
    cells: Array<DataTableCellUpsert>,
    options?: DataTableCellsUpdateOptions
  ): Promise<DataTableCellUpdateEventDetail>;

  validate(): void;

  validateColumn(...columns: Array<DataTableColumnApiId>): void;

  selectCell(cellCoordinate: DataTableCellIndexCoordinate, options?: DataTableCellSelectionOptions): void;

  moveSelection(direction: DataTableDirection, options?: DataTableMoveSelectionOptions): void;

  selectRow(rowIndex: number): void;

  selectColumn(columnIndex: number): void;

  clearSelection(): Promise<void>;

  hasSelectedCells(): boolean;

  subscribe<Event extends DataTableEvent>(event: Event, listener: DataTableListener[Event], signal?: AbortSignal): void;

  unsubscribe<Event extends DataTableEvent>(event: Event, listener: DataTableListener[Event]): void;

  destroy(): void;

  undo(): Promise<DataTableCellUpdateEventDetail>;

  redo(): Promise<DataTableCellUpdateEventDetail>;

  tableValid(): boolean;

  cellStatusOverview(): Record<DataTableCellStatusType, number>;

  undoEnabled(): boolean;

  redoEnabled(): boolean;

  rowHeaders(): Array<Omit<DataTableRowHeader, 'displayed'>>;

  displayedRowHeaderTitles(): Array<string>;

  rowHeaderWidth(): number;

  displayedRowHeaderIds(): Array<DataTableDisplayColumnType>;

  updateDisplayedRowHeaders(
    displayedHeaderIds: Set<DataTableDisplayColumnType>,
    options?: DataTableUpdateDisplayedRowHeadersOptions
  ): Promise<void>;

  /**
   * Updates the expansion state of column groups.
   *
   * @param actions - Array of expand/collapse actions to apply
   * @returns Promise that resolves when update is complete
   *
   * @remarks
   * This is currently a stub implementation. The backend API is work-in-progress.
   * When implemented, the server will control which child columns are displayed,
   * as not all child columns may be hidden on collapse.
   */
  updateColumnGroupExpansion(...actions: Array<DataTableColumnGroupExpansionAction>): Promise<void>;

  moveColumn(columnAddRequest: DataTableColumnMoveRequest): Promise<DataTableMoveColumnResponse>;

  addColumn(columnAddRequest: DataTableColumnAddRequest): Promise<Array<DataTableColumn>>;

  removeColumn(columnId: DataTableColumnApiId): Promise<void>;

  addRow(rowAddRequest: DataTableRowAddRequest): Promise<Array<DataTableRow>>;

  removeRow(rowId: DataTableRowApiId): Promise<void>;

  updateColumn(columnUpdate: DataTableColumnUpdateRequest): Promise<DataTableColumn>;

  fillColumn(columnId: DataTableColumnApiId, fillData: DataTableColumnFillRequestV1): Promise<void>;

  windowScrollChange(rect: DataTableRect): void;

  getColumnGroupingData(): DataTableColumnGroupingData | null;

  rightBorderType(columnIndex: DataTableColumnIndex): DataTableRightBorderType;

  getPinnedColumns(): Readonly<Array<PinnedColumn>>;
}

export interface DataTableRect {
  get top(): number;

  get bottom(): number;

  get left(): number;

  get right(): number;

  get width(): number;

  get height(): number;

  get area(): number;

  contains(value: DataTableRect): boolean;
}

export interface DataTableSelection {
  get from(): Readonly<DataTableCellIndexCoordinate>;

  get to(): Readonly<DataTableCellIndexCoordinate>;

  get topLeft(): Readonly<DataTableCellIndexCoordinate>;

  get bottomRight(): Readonly<DataTableCellIndexCoordinate>;

  get rect(): Readonly<DataTableRect>;

  contains(coordinate: DataTableCellIndexCoordinate): boolean;

  containsColumn(column: number): boolean;

  containsRow(row: number): boolean;
}

export interface DataTableCellSelection<Column = unknown, Row = unknown> {
  from: DataTableCellCoordinate<Column, Row>;
  to: DataTableCellCoordinate<Column, Row>;
}

export interface DataTableRowHeader {
  id: DataTableDisplayColumnType;
  title: string;
  displayed: boolean;
}

export type PinnedColumn = { type: 'dataColumn'; id: DataTableColumnApiId };

export interface DataTableServiceProps {
  apiService: DataTableApiService;
  readonly: boolean;
  rowHeaders: Array<DataTableRowHeader>;
  dimensions: DataTableDimensions;
  studyApiId: StudyApiId;
  dataTableApiId?: DataTableApiId;
  realtimeEnabled?: boolean;
  pinnedColumns?: Array<PinnedColumn>;
}

export type DataTableColumnType = components['schemas']['DataTableColumnV1.schema']['type'];

export type DataTableColumnV1 = components['schemas']['DataTableColumnV1.schema'];
export type DataTableColumnCreateV1 = components['schemas']['DataTableColumnCreateV1.schema'];
export type DataTableColumnUpdateV1 = components['schemas']['DataTableColumnUpdateV1.schema'];
export type DataTableColumnUpdateV1SampleDoseTime =
  components['schemas']['DataTableColumnUpdateV1SampleDoseTime.schema'];
/**
 * Column types that support the updateColumn operation.
 * Derived from the DataTableColumnUpdateV1 schema.
 */
export type DataTableUpdatableColumnType = DataTableColumnUpdateV1['type'];

/**
 * Record of updatable column types for runtime type checking.
 */
const updatableColumnTypes: Record<DataTableUpdatableColumnType, true> = {
  number: true,
  text: true,
  timestamp: true,
  timestampBaseline: true,
  timestampBaselineRelative: true,
  formula: true,
  measurement: true,
  observation: true,
  sample: true,
  sampleCollectedAt: true,
  sampleDetail: true,
  sampleDoseTime: true,
  sampleComments: true,
  metadata: true,
};

/**
 * Type guard to check if a column type supports the updateColumn operation.
 * @param type - The column type to check.
 * @returns boolean Whether the column type supports updates.
 */
export const isUpdatableColumnType = (type: Nullable<DataTableColumnType>): type is DataTableUpdatableColumnType =>
  _notNil(type) && type in updatableColumnTypes;

export interface DataTableColumnAddRequest {
  type: DataTableColumnType;
  name?: string | null;
  unit?: string | null;
  parent_api_id?: DataTableColumnApiId | null;
  measurement?: Partial<DataTableMeasurement> & Pick<DataTableMeasurement, 'measured_at'>;
  observation?: Partial<DataTableObservation> & Pick<DataTableObservation, 'observed_at'>;
  formula?: DataTableFormula;
  pk?: {
    interval?: number | null;
    interval_time_unit?: string | null;
    clash_window?: string | null;
    from?: string | null;
    timestamp_baseline_id?: DataTableColumnApiId | null;
  };
}

/**
 * Client-side type for column updates that combines the generated OpenAPI type with an `id` field.
 * The `id` is used for client-side routing but not sent in the request body (it's in the URL path).
 */
export type DataTableColumnUpdateRequest = DataTableColumnUpdateV1 & {
  id: DataTableColumnApiId;
};

export type DataTableColumnMoveRequest = components['schemas']['DataTableColumnUpdateOrderV1.schema'];

export interface DataTableRowAddRequest {
  animals: Array<AnimalApiId>;
}

export type DataTableFormula = components['schemas']['DataTableColumnFormulaV1.schema'];

/**
 * Augmented DataTableMeasurement type with client-side fields.
 * Note: is_calculated is required for UI logic but NOT in OpenAPI schema.
 */
export interface DataTableMeasurement {
  metric: string;
  measured_at: string;
  is_calculated: boolean; // Client-side only field
}

export type DataTableObservation = components['schemas']['DataTableColumnObservationV1.schema'];

export type DataTableMetadataGlossaryFieldType = components['schemas']['MetadataGlossaryFieldType.schema'];

export type DataTableMetadataFieldType = Extract<DataTableMetadataGlossaryFieldType, 'select' | 'multi_select'>;

export const selectFieldTypeConfig = {
  select: { supportsMultiple: false },
  multi_select: { supportsMultiple: true },
} as const satisfies Readonly<Record<DataTableMetadataFieldType, { supportsMultiple: boolean }>>;

export interface DataTableWindowClearRequest {
  startColumnId: string;
  startRowId: string;
  endColumnId: string;
  endRowId: string;
}

/**
 * Helper type to augment measurement columns with is_calculated field
 */
type AugmentMeasurementColumn<T> = T extends { type: 'measurement'; measurement: infer M }
  ? Omit<T, 'measurement'> & { measurement: M & { is_calculated: boolean } }
  : T;

/**
 * DataTableColumn type with client-side augmentation.
 * Extends OpenAPI schema with:
 * - measurement.is_calculated: UI-only field for measurement columns
 *
 * Note: reference_date is now part of the OpenAPI schema for measurement/observation columns
 */
export type DataTableColumn = AugmentMeasurementColumn<components['schemas']['DataTableColumnV1.schema']>;

export type FillableColumn = Extract<
  DataTableColumn,
  { type: 'sampleDoseTime' | 'sampleCollectedAt' | 'timestampBaseline' }
>;

/**
 * Type representing a child column - extracts only the column types from the DataTableColumn
 * discriminated union that have a parent_api_id field in their schema, then narrows it to
 * ensure parent_api_id is a valid (non-null, non-undefined) DataTableColumnApiId value.
 *
 * This ensures type safety when working with child columns in column grouping scenarios by:
 * 1. Only including column types that support parent_api_id in the OpenAPI schema
 * 2. Guaranteeing that the parent_api_id property is a valid API ID (not null/undefined)
 */
export type ChildColumn = Extract<DataTableColumn, { parent_api_id?: DataTableColumnApiId | null }> & {
  parent_api_id: DataTableColumnApiId;
};

/**
 * Helper type to extract metadata columns from DataTableColumn union
 */
export type DataTableMetadataColumn = components['schemas']['DataTableColumnV1Metadata.schema'];

export interface DataTableColumnGroup {
  parentColumn: DataTableColumn;
  childColumns: Array<DataTableColumn>;
  startIndex: number;
  colspan: number;
}

export interface DataTableColumnGroupingData {
  leafColumns: Array<DataTableColumn>;
  columnGroups: Array<DataTableColumnGroup>;
  columnGroupMap: Map<number, DataTableColumnGroup>;
  groupStartSet: Set<number>;
  groupEndSet: Set<number>;
}

export type DataTableRow = components['schemas']['DataTableRowV1.schema'];

export interface DataTableCellData {
  value: string | null;
}

export interface DataTableCellDataCache extends DataTableCellData {
  updated_at: ISODateTime | null;
}

export interface DataTableCellAlertCache extends DataTableCellIdCoordinate {
  alerts: Map<ID, AnimalAlertV1>;
}

export interface DataTableCellCoordinate<Column = unknown, Row = unknown> {
  column: Column;
  row: Row;
}

export const DataTableCellSelectionState = {
  Highlighted: 'Highlighted',
  OriginSelected: 'OriginSelected',
  NotSelected: 'NotSelected',
} as const;
export type DataTableCellSelectionState =
  (typeof DataTableCellSelectionState)[keyof typeof DataTableCellSelectionState];

export type DataTableCellIdCoordinate = DataTableCellCoordinate<DataTableColumnApiId, DataTableRowApiId>;
export type DataTableCellIndexCoordinate = DataTableCellCoordinate<number, number>;
export type DataTableCellState = 'Loading' | 'Saving' | 'Valid' | 'Invalid' | 'NotSaved' | 'Unknown' | 'Warning';

export type DataTableCellUpsert = { column_id: DataTableColumnApiId; row_id: DataTableRowApiId } & DataTableCellData;
export type DataTableCellUpsertError = DataTableCellUpsert & { error: string };

export interface DataTableAlert {
  column_id: DataTableColumnApiId;
  row_id: DataTableRowApiId;
  alerts: Array<AnimalAlertV1>;
}

export interface DataTableApiLoadCellResponse extends DataTableAlert {
  cell: DataTableCellData;
  created_at: ISODateTime;
  updated_at: ISODateTime;
}

export type DataTableCellUpsertResponse =
  | { success: true; effects?: Array<DataTableColumnApiId>; data_table_alerts?: Array<DataTableAlert> }
  | {
      success: false;
      errors: Array<DataTableCellUpsertError>;
      effects?: Array<DataTableColumnApiId>;
      data_table_alerts?: Array<DataTableAlert>;
    };

export type DataTableValidateResponse =
  | { success: true; errors: Array<DataTableCellUpsertError> }
  | { success: false; errors: Array<DataTableCellUpsertError> };

export type DataTableSelectionUpdate = components['schemas']['DataTableSelectionUpdateV1.schema'];

export type DataTableMoveColumnResponse = {
  id: DataTableColumnApiId;
  columns: Array<DataTableColumn>;
  rows: Array<DataTableRow>;
  name: string;
  type: string;
};

export type DataTableWindowClearResponse =
  | { success: true; effects: Array<DataTableColumnApiId> }
  | { success: false; errors: Array<DataTableCellUpsertError>; effects: Array<DataTableColumnApiId> };

export interface DataTableColumnRemoveResponse {
  deleted: boolean;
  effects: Array<DataTableColumnApiId>;
  dependencies: Array<DataTableColumnApiId>;
}

export type DataTableColumnUpdateEventDetail = DataTableColumn;
export type DataTableCellUpdateEventDetail = Record<DataTableCellReference, DataTableCellDataCache>;

export interface DataTableCellAlertUpdateEventDetail {
  cellAlerts: Record<DataTableCellReference, DataTableCellAlertCache>;
  trigger: 'cell-update' | 'cell-load';
}

export interface DataTableCellSelectionChangeEventDetail {
  source: 'api' | 'user';
  selection: Readonly<DataTableSelection>;
  scrollTo: boolean;
  activate: boolean;
  content?: string;
  options?: { direction?: DataTableDirection };
}

interface DataTableUndoRedoStateChangeEventDetail {
  redoEnabled: boolean;
  undoEnabled: boolean;
}

export type DataTableCellStatusType =
  | 'OK'
  | 'ValidationError'
  | 'NetworkError'
  | 'ValidationWarning'
  | 'AsyncValidationWarning';

export type DataTableCellOkStatus = { type: 'OK' };

export type DataTableCellValidationErrorStatus = {
  type: 'ValidationError';
  error: string;
};

export type DataTableCellValidationWarningStatus = {
  type: 'ValidationWarning';
  error: string;
};

export type DataTableCellNetworkErrorStatus = {
  type: 'NetworkError';
};

export type DataTableCellAsyncValidationWarningStatus = {
  type: 'AsyncValidationWarning';
  error: string;
};

export type DataTableCellStatus =
  | DataTableCellOkStatus
  | DataTableCellValidationErrorStatus
  | DataTableCellValidationWarningStatus
  | DataTableCellAsyncValidationWarningStatus
  | DataTableCellNetworkErrorStatus;

export interface DataTableCellStateChangeEventDetail {
  invalidColumns: Set<DataTableColumnApiId>;
  invalidRows: Set<DataTableRowApiId>;
  invalidCells: Set<DataTableCellReference>;
  overview: Record<DataTableCellStatusType, number>;
}

export interface DataTableRowHeaderStateChangeEventDetail {
  displayedRowHeaderIds: Set<DataTableDisplayColumnType>;
  rowHeaderWidth: number;
}

export type DataTableStateChangeType = keyof DataTable | '*';

export interface DataTableStateChangeEventDetail {
  type: Readonly<Set<DataTableStateChangeType>>;
  ids: Readonly<Set<DataTableColumnApiId | DataTableRowApiId>>;
}

export interface DataTableSavingEventDetail {
  saving: boolean;
}

export interface DataTableHighVolumeUpsertDetail {
  totalChunkedRequests: number;
  pending: Array<Array<DataTableCellUpsert>>;
  resolved: Array<Array<DataTableCellUpsert>>;
  rejected: Array<Array<DataTableCellUpsert>>;
}

export interface DataTableWorkflowActivatedDetail {
  active: boolean;
}

export interface DataTableMoveColumnEventDetail {
  columnId: DataTableColumnApiId;
}

export type DataTableColumnUpdateEvent = CustomEvent<DataTableColumnUpdateEventDetail>;
export type DataTableCellUpdateEvent = CustomEvent<DataTableCellUpdateEventDetail>;
export type DataTableCellAlertUpdateEvent = CustomEvent<DataTableCellAlertUpdateEventDetail>;
export type DataTableCellSelectionChangeEvent = CustomEvent<DataTableCellSelectionChangeEventDetail>;
export type DataTableUndoRedoStateChangeEvent = CustomEvent<DataTableUndoRedoStateChangeEventDetail>;
export type DataTableCellStateChangeEvent = CustomEvent<DataTableCellStateChangeEventDetail>;
export type DataTableRowHeaderStateChangeEvent = CustomEvent<DataTableRowHeaderStateChangeEventDetail>;
export type DataTableStateChangeEvent = CustomEvent<DataTableStateChangeEventDetail>;
export type DataTableSavingEvent = CustomEvent<DataTableSavingEventDetail>;
export type DataTablePasteLimitExceededEvent = CustomEvent<void>;
export type DataTableHighVolumeUpsertEvent = CustomEvent<DataTableHighVolumeUpsertDetail>;
export type DataTableMoveColumnEvent = CustomEvent<DataTableMoveColumnEventDetail>;
export type DataTableWorkflowEvent = CustomEvent<DataTableWorkflowAction>;
export type DataTableWorkflowActivatedEvent = CustomEvent<DataTableWorkflowActivatedDetail>;

export const DataTableEvent = {
  ColumnUpdate: 'ColumnUpdate',
  CellUpdate: 'CellUpdate',
  CellAlertUpdate: 'CellAlertUpdate',
  CellSelectionChange: 'CellSelectionChange',
  UndoRedoStateChange: 'UndoRedoStateChange',
  CellStateChange: 'CellStateChange',
  TableStateChange: 'TableStateChange',
  RowHeaderStateChange: 'RowHeaderStateChange',
  TableSaving: 'TableSaving',
  PasteLimitExceeded: 'PasteLimitExceeded',
  HighVolumeUpsert: 'HighVolumeUpsert',
  MoveColumn: 'MoveColumn',
  WorkflowActionEnter: 'WorkflowActionEnter',
  WorkflowActionExit: 'WorkflowActionExit',
  WorkflowActivated: 'WorkflowActivated',
  ClearValidation: 'ClearValidation',
  RemoteSelectionChange: 'RemoteSelectionChange',
  ActiveUsersChange: 'ActiveUsersChange',
} as const;
export type DataTableEvent = (typeof DataTableEvent)[keyof typeof DataTableEvent];

export type DataTableRemoteSelectionChangeEvent = CustomEvent<DataTableEventV1SelectionChange>;
export type DataTableActiveUsersChangeEvent = CustomEvent<DataTableEventV1ActiveUsers>;

export type DataTableListener = {
  [DataTableEvent.ColumnUpdate]: (event: DataTableColumnUpdateEvent) => void;
  [DataTableEvent.CellUpdate]: (event: DataTableCellUpdateEvent) => void;
  [DataTableEvent.CellAlertUpdate]: (event: DataTableCellAlertUpdateEvent) => void;
  [DataTableEvent.CellSelectionChange]: (event: DataTableCellSelectionChangeEvent) => void;
  [DataTableEvent.UndoRedoStateChange]: (event: DataTableUndoRedoStateChangeEvent) => void;
  [DataTableEvent.CellStateChange]: (event: DataTableCellStateChangeEvent) => void;
  [DataTableEvent.RowHeaderStateChange]: (event: DataTableRowHeaderStateChangeEvent) => void;
  [DataTableEvent.TableStateChange]: (event: DataTableStateChangeEvent) => void;
  [DataTableEvent.TableSaving]: (event: DataTableSavingEvent) => void;
  [DataTableEvent.PasteLimitExceeded]: (event: DataTablePasteLimitExceededEvent) => void;
  [DataTableEvent.HighVolumeUpsert]: (event: DataTableHighVolumeUpsertEvent) => void;
  [DataTableEvent.MoveColumn]: (event: DataTableMoveColumnEvent) => void;
  [DataTableEvent.WorkflowActionEnter]: (event: DataTableWorkflowEvent) => void;
  [DataTableEvent.WorkflowActionExit]: (event: DataTableWorkflowEvent) => void;
  [DataTableEvent.WorkflowActivated]: (event: DataTableWorkflowActivatedEvent) => void;
  [DataTableEvent.ClearValidation]: () => void;
  [DataTableEvent.RemoteSelectionChange]: (event: DataTableRemoteSelectionChangeEvent) => void;
  [DataTableEvent.ActiveUsersChange]: (event: DataTableActiveUsersChangeEvent) => void;
};

export type DataTableValidatorResponse = { valid: true } | { valid: false; error: string };

export interface DataTableValidatorFunction {
  (value: string | null, metadata?: { field_type?: DataTableMetadataGlossaryFieldType }): DataTableValidatorResponse;
}

interface DataTableWorkflowActionBase<Type extends string, Source extends string, Destination extends string> {
  type: Type;
  source: Source;
  destination: Destination;
}

export type DataTableWorkflowAction =
  | DataTableWorkflowActionBase<'OpenSearch', 'search', DataTableColumnApiId>
  | DataTableWorkflowActionBase<'NextAnimal', 'next-animal', DataTableColumnApiId>
  | DataTableWorkflowActionBase<'End', DataTableColumnApiId, 'end'>
  | DataTableWorkflowActionBase<'SelectColumn', DataTableColumnApiId, DataTableColumnApiId>;

export type DataTableWorkflowActionSource = DataTableWorkflowAction['source'];

export interface LoadWorkflowOptions {
  persist?: boolean;
}

export type DataTableWorkflow = Array<DataTableWorkflowAction>;

export interface WorkflowActionsExitOptions {
  targetRowIndex?: number;
}

export interface DataTableWorkflowService {
  get workflowActive(): Readonly<boolean>;

  get workflow(): Readonly<DataTableWorkflow>;

  loadWorkflow(workflow: DataTableWorkflow, options?: LoadWorkflowOptions): void;

  activateWorkflow(active: boolean): void;

  clearWorkflow(): void;

  workflowValid(workflow: DataTableWorkflow): boolean;

  workflowActionEnter(source: DataTableWorkflowActionSource | undefined): boolean;

  workflowActionExit(source: DataTableWorkflowActionSource | undefined, options?: WorkflowActionsExitOptions): boolean;

  workflowLoaded(): boolean;

  sourceInWorkflow(source: DataTableWorkflowActionSource): boolean;
}
